/**
 * Zero-knowledge vault import / export.
 *
 * IMPORT: parses a CSV exported by another manager (generic format + common
 * mappings: Bitwarden, Chrome/browser) to `VaultItemPlain[]`. Encryption to the
 * vault occurs AFTERWARDS in the client (with `crypto.encryptItem`): these
 * functions never touch the server or keys.
 *
 * Encrypted EXPORT (zero-knowledge backup): seals ALL items in a blob
 * that can only be opened with a passphrase (Argon2id → backup key → AES-GCM).
 * The server never sees it; it is portable and re-importable (round-trip tested).
 *
 * Plaintext EXPORT (portability): CSV readable by other managers, PROTECTED
 * against CSV formula injection (cells starting with = + - @ / TAB / CR
 * are neutralized with `'`). Requires strong confirmation in the UI (exposes plaintext).
 *
 * INVARIANT (tested in `__tests__/import-export.test.ts`): the encrypted backup
 * blob does NOT contain any plaintext or the backup key, in any
 * encoding (base64/hex). Round-trip import→export→import preserves data.
 */

import { base64ToBytes, bytesToBase64, utf8Decode, utf8Encode } from "../crypto/encoding";
import {
  type Argon2Params,
  deriveMasterKey,
  type KdfConfig,
  KDF_PRESETS,
  newKdfSaltBytes,
} from "../crypto/kdf";
import { hkdfExpand, openBase64, sealBase64 } from "../crypto/primitives";
import type { VaultItemPlain, VaultItemType } from "../crypto/types";

// ─────────────────────────────────────────────────────────────────────────────
// CSV parsing (RFC 4180: double quotes, escaped commas and newlines)
// ─────────────────────────────────────────────────────────────────────────────

/** Parses a CSV into a matrix of cell rows (respects quotes/escapes). */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  // Normalize CRLF/CR to LF outside quotes to avoid incorrect splits.
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // Last cell/row (if the file does not end with \n).
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Discard entirely empty rows.
  return rows.filter((r) => r.some((cell) => cell.trim().length > 0));
}

export interface CsvRecords {
  readonly headers: string[];
  readonly records: Record<string, string>[];
}

/** Converts CSV rows into per-header records (first row = headers). */
export function csvToRecords(text: string): CsvRecords {
  const rows = parseCsv(text);
  const [headerRow, ...dataRows] = rows;
  if (!headerRow) {
    return { headers: [], records: [] };
  }
  const headers = headerRow.map((h) => h.trim().toLowerCase());
  const records = dataRows.map((r) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rec[h] = r[idx] ?? "";
    });
    return rec;
  });
  return { headers, records };
}

// ─────────────────────────────────────────────────────────────────────────────
// Format detection + mapping to VaultItemPlain
// ─────────────────────────────────────────────────────────────────────────────

export type CsvImportFormat = "bitwarden" | "chrome" | "generic";

/** Deduces the CSV format from its headers. */
export function detectCsvFormat(headers: string[]): CsvImportFormat {
  const set = new Set(headers);
  if (set.has("login_password") || set.has("login_username") || set.has("login_uri")) {
    return "bitwarden";
  }
  // Chrome/browsers: name,url,username,password (without login_* columns).
  if (set.has("password") && set.has("url") && (set.has("name") || set.has("username"))) {
    return "chrome";
  }
  return "generic";
}

function pick(rec: Record<string, string>, ...keys: string[]): string {
  for (const k of keys) {
    const v = rec[k];
    if (v && v.trim().length > 0) {
      return v;
    }
  }
  return "";
}

function toPlain(
  type: VaultItemType,
  name: string,
  fields: Record<string, string>,
  extra: { totpSecret?: string; notes?: string } = {},
): VaultItemPlain {
  // Discard empty fields (does not pollute envelope).
  const clean: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    if (v && v.length > 0) {
      clean[k] = v;
    }
  }
  return {
    type,
    name,
    fields: clean,
    ...(extra.totpSecret ? { totpSecret: extra.totpSecret } : {}),
    ...(extra.notes ? { notes: extra.notes } : {}),
  };
}

function mapRecord(format: CsvImportFormat, rec: Record<string, string>): VaultItemPlain | null {
  if (format === "bitwarden") {
    const type = pick(rec, "type").toLowerCase();
    if (type === "note" || type === "securenote") {
      const name = pick(rec, "name") || "Nota";
      return toPlain("note", name, {}, { notes: pick(rec, "notes") });
    }
    const username = pick(rec, "login_username");
    const password = pick(rec, "login_password");
    const url = pick(rec, "login_uri");
    const name = pick(rec, "name") || url || username;
    if (!name && !username && !password) {
      return null;
    }
    return toPlain(
      "login",
      name || "Login",
      { username, password, url },
      { totpSecret: pick(rec, "login_totp"), notes: pick(rec, "notes") },
    );
  }
  if (format === "chrome") {
    const username = pick(rec, "username");
    const password = pick(rec, "password");
    const url = pick(rec, "url");
    const name = pick(rec, "name") || url || username;
    if (!name && !username && !password) {
      return null;
    }
    return toPlain("login", name || "Login", { username, password, url });
  }
  // Generic: headers name/username/password/url/totp/notes (case-insensitive).
  const username = pick(rec, "username", "user", "login");
  const password = pick(rec, "password", "pass", "pwd");
  const url = pick(rec, "url", "uri", "website");
  const totp = pick(rec, "totp", "otp", "otpauth");
  const notes = pick(rec, "notes", "note", "comment");
  const name = pick(rec, "name", "title") || url || username;
  const hasLogin = Boolean(username || password || url);
  if (!name && !hasLogin && !notes) {
    return null;
  }
  if (!hasLogin && notes) {
    return toPlain("note", name || "Nota", {}, { notes });
  }
  return toPlain(
    "login",
    name || "Login",
    { username, password, url },
    { totpSecret: totp, notes },
  );
}

export interface CsvImportResult {
  readonly format: CsvImportFormat;
  readonly items: VaultItemPlain[];
  /** Rows discarded because they are empty / contain no usable data. */
  readonly skipped: number;
}

/**
 * Imports a CSV to plaintext items (`VaultItemPlain[]`) ready to encrypt to the
 * vault in the client. `format` is auto-detected if omitted.
 */
export function importCsv(text: string, format?: CsvImportFormat): CsvImportResult {
  const { headers, records } = csvToRecords(text);
  const fmt = format ?? detectCsvFormat(headers);
  const items: VaultItemPlain[] = [];
  let skipped = 0;
  for (const rec of records) {
    const plain = mapRecord(fmt, rec);
    if (plain) {
      items.push(plain);
    } else {
      skipped++;
    }
  }
  return { format: fmt, items, skipped };
}

// ─────────────────────────────────────────────────────────────────────────────
// Encrypted export (zero-knowledge backup, re-importable with passphrase)
// ─────────────────────────────────────────────────────────────────────────────

const BACKUP_KIND = "boveda-backup" as const;
const BACKUP_VERSION = 1 as const;
const BACKUP_AAD = "boveda:backup:v1";
const BACKUP_HKDF_INFO = "boveda:backup:v1:key";

/** Format of the encrypted backup blob (portable, opaque). */
export interface EncryptedBackup {
  readonly kind: typeof BACKUP_KIND;
  readonly v: typeof BACKUP_VERSION;
  /** Public KDF parameters to re-derive the key from the passphrase. */
  readonly kdf: KdfConfig;
  /** AES-256-GCM envelope (base64) of serialized items. */
  readonly blob: string;
}

/** Derives the backup key (32 B) from the passphrase + public KDF. */
async function deriveBackupKey(passphrase: string, kdf: KdfConfig): Promise<Uint8Array> {
  const master = deriveMasterKey(passphrase, base64ToBytes(kdf.salt), kdf.params);
  try {
    return await hkdfExpand(master, BACKUP_HKDF_INFO);
  } finally {
    master.fill(0);
  }
}

/**
 * Seals all items in a backup encrypted with a passphrase. Uses the
 * `sensitive` Argon2id preset (high-value operation). Result is opaque: only
 * holder of the passphrase can open it.
 */
export async function exportEncryptedBackup(
  passphrase: string,
  items: readonly VaultItemPlain[],
  params: Argon2Params = KDF_PRESETS.sensitive,
): Promise<EncryptedBackup> {
  if (passphrase.length === 0) {
    throw new Error("passphrase required");
  }
  const salt = newKdfSaltBytes();
  const kdf: KdfConfig = { algorithm: "argon2id", params, salt: bytesToBase64(salt), v: 1 };
  const key = await deriveBackupKey(passphrase, kdf);
  try {
    const blob = await sealBase64(key, utf8Encode(JSON.stringify(items)), BACKUP_AAD);
    return { kind: BACKUP_KIND, v: BACKUP_VERSION, kdf, blob };
  } finally {
    key.fill(0);
  }
}

/** Opens an encrypted backup with its passphrase → plaintext items (client ONLY). */
export async function importEncryptedBackup(
  passphrase: string,
  backup: EncryptedBackup,
): Promise<VaultItemPlain[]> {
  if (backup.kind !== BACKUP_KIND || backup.v !== BACKUP_VERSION) {
    throw new Error("unrecognized backup format");
  }
  const key = await deriveBackupKey(passphrase, backup.kdf);
  try {
    const bytes = await openBase64(key, backup.blob, BACKUP_AAD);
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    return JSON.parse(utf8Decode(bytes)) as VaultItemPlain[];
  } finally {
    key.fill(0);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Plaintext export (CSV) — with formula injection protection
// ─────────────────────────────────────────────────────────────────────────────

const PLAINTEXT_HEADERS = ["name", "type", "username", "password", "url", "totp", "notes"] as const;

/**
 * Neutralizes CSV formula injection (CWE-1236): a cell starting with
 * `= + - @` or TAB/CR would execute as a formula when opened in a spreadsheet.
 * Prepend `'` to force interpretation as text.
 */
export function sanitizeCsvCell(value: string): string {
  if (value.length > 0 && /^[=+\-@\t\r]/.test(value)) {
    return `'${value}`;
  }
  return value;
}

/** Quotes a CSV cell (RFC 4180) after neutralizing formula injection. */
function csvField(value: string): string {
  const safe = sanitizeCsvCell(value);
  if (/[",\n\r]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

/**
 * Exports items to PLAINTEXT CSV (portability). Dangerous: exposes secrets.
 * UI MUST require strong confirmation. Protected against formula injection.
 */
export function exportPlaintextCsv(items: readonly VaultItemPlain[]): string {
  const lines = [PLAINTEXT_HEADERS.join(",")];
  for (const item of items) {
    const cells = [
      item.name,
      item.type,
      item.fields.username ?? "",
      item.fields.password ?? "",
      item.fields.url ?? "",
      item.totpSecret ?? "",
      item.notes ?? "",
    ];
    lines.push(cells.map(csvField).join(","));
  }
  return lines.join("\n");
}
